import { useState } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import type { QuestionAnswersFilter } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuestionAnswers } from '@/hooks/queries';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { QuestionAnswersTable } from './QuestionAnswersTable';
import type { SortDirection } from './SortIcon';

// Radix Select forbids empty-string values (reserved for clearing), so use a sentinel.
const ALL_ANSWERS = '__all__';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

type SortField = NonNullable<QuestionAnswersFilter['sortBy']>;

export interface QuestionAnswersPageProps {
  questionId: string;
  campaignId: string;
  filter: QuestionAnswersFilter;
  canManageAnalytics: boolean;
  onFilterChange: (next: QuestionAnswersFilter) => void;
}

function buildExportQueryString(filter: QuestionAnswersFilter): string {
  const params = new URLSearchParams();
  if (filter.answer) params.set('answer', filter.answer);
  if (filter.dateFrom) params.set('dateFrom', filter.dateFrom);
  if (filter.dateTo) params.set('dateTo', filter.dateTo);
  if (filter.sortBy) params.set('sortBy', filter.sortBy);
  if (filter.sortOrder) params.set('sortOrder', filter.sortOrder);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function QuestionAnswersPage({
  questionId,
  campaignId,
  filter,
  canManageAnalytics,
  onFilterChange,
}: QuestionAnswersPageProps) {
  const { data, isLoading, isError } = useQuestionAnswers(questionId, filter);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const page = filter.page ?? 1;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;
  const sortBy: SortField = filter.sortBy ?? 'contactTimestamp';
  const sortOrder: SortDirection = filter.sortOrder ?? 'desc';

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const isLastPage = page >= totalPages;
  const isFirstPage = page <= 1;

  const updateFilter = (patch: Partial<QuestionAnswersFilter>): QuestionAnswersFilter => ({
    ...filter,
    ...patch,
  });

  const handleAnswerChange = (next: string) => {
    onFilterChange(
      updateFilter({
        answer: next === ALL_ANSWERS ? undefined : next,
        page: 1,
      })
    );
  };

  const handleDateFromChange = (next: string) => {
    onFilterChange(updateFilter({ dateFrom: next || undefined, page: 1 }));
  };

  const handleDateToChange = (next: string) => {
    onFilterChange(updateFilter({ dateTo: next || undefined, page: 1 }));
  };

  const handlePageSizeChange = (next: string) => {
    onFilterChange(updateFilter({ pageSize: Number(next), page: 1 }));
  };

  const handlePageChange = (nextPage: number) => {
    onFilterChange(updateFilter({ page: nextPage }));
  };

  const handleSortChange = (field: SortField, order: SortDirection) => {
    onFilterChange(updateFilter({ sortBy: field, sortOrder: order, page: 1 }));
  };

  const handleExport = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const qs = buildExportQueryString(filter);
      const url = `${API_BASE_URL}/api/campaigns/${campaignId}/voter-segments/by-answer/${questionId}/export${qs}`;
      const response = await authService.authenticatedFetch(url);
      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filenameFromHeader(response.headers.get('content-disposition'))
        ?? defaultFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const breakdownAnswers = data?.question.breakdowns.map((b) => b.answer) ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {data?.question.questionText ?? 'Question Answers'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {totalCount.toLocaleString()} {totalCount === 1 ? 'answer' : 'answers'}
          </p>
        </div>
        {canManageAnalytics && (
          <Button onClick={handleExport} disabled={isExporting}>
            <Download />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        )}
      </header>

      {exportError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {exportError}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="filter-answer">Answer</Label>
          <Select value={filter.answer ?? ALL_ANSWERS} onValueChange={handleAnswerChange}>
            <SelectTrigger id="filter-answer" className="w-48">
              <SelectValue placeholder="All answers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ANSWERS}>All answers</SelectItem>
              {breakdownAnswers.map((answer) => (
                <SelectItem key={answer} value={answer}>
                  {answer}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="filter-date-from">From</Label>
          <Input
            id="filter-date-from"
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={(e) => handleDateFromChange(e.target.value)}
            className="w-40"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="filter-date-to">To</Label>
          <Input
            id="filter-date-to"
            type="date"
            value={filter.dateTo ?? ''}
            onChange={(e) => handleDateToChange(e.target.value)}
            className="w-40"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="filter-page-size">Per page</Label>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger id="filter-page-size" className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError ? (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load question answers. Please try again.
        </div>
      ) : (
        <QuestionAnswersTable
          rows={data?.items ?? []}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(page - 1)}
            disabled={isFirstPage}
            aria-label="Previous page"
          >
            <ChevronLeft />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(page + 1)}
            disabled={isLastPage}
            aria-label="Next page"
          >
            Next
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}

function defaultFilename(): string {
  const today = new Date().toISOString().slice(0, 10);
  return `question-answers-${today}.csv`;
}

function filenameFromHeader(header: string | null): string | undefined {
  if (!header) return undefined;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1];
}
